import { useCountUp } from "../../lib/useCountUp.js";
import { useT, type Translate } from "../../i18n/index.js";

export interface PodiumEntry {
  rank: number;
  playerId: string;
  nickname: string;
  score: number;
}

/** Reveal delays in ms — 3rd first, winner last, for suspense on the TV. */
const REVEAL_DELAY = { third: 400, second: 1400, first: 2600, rest: 3400 } as const;

function CountUpScore({ score, delay }: { score: number; delay: number }) {
  const displayed = useCountUp(score, delay);
  return <>{displayed.toLocaleString()} pts</>;
}

function PodiumColumn({ entry, place, t }: { entry: PodiumEntry | undefined; place: 1 | 2 | 3; t: Translate }) {
  if (!entry) return <div className="podium-col podium-col--empty" aria-hidden="true" />;

  const delay =
    place === 1 ? REVEAL_DELAY.first : place === 2 ? REVEAL_DELAY.second : REVEAL_DELAY.third;

  return (
    <div className={`podium-col podium-col--${place}`}>
      <div className="podium-player">
        <span className="podium-name">{entry.nickname}</span>
        <span className="podium-score">
          <CountUpScore score={entry.score} delay={delay + 200} />
        </span>
      </div>
      <div className="podium-block">
        <span className="podium-place">{place === 1 ? t("hostGame.firstPlace") : place === 2 ? t("hostGame.secondPlace") : t("hostGame.thirdPlace")}</span>
      </div>
    </div>
  );
}

/**
 * Final-results ceremony for the shared host screen. The top three rise one
 * by one (3rd, 2nd, then the winner), followed by the rest of the field.
 * All motion is transform/opacity; reduced-motion users see everything at once.
 */
/** Ranks the final screen shows: the three on the stage plus the rows below. */
export const PODIUM_SIZE = 10;

export function PodiumCeremony({ entries }: { entries: PodiumEntry[] }) {
  const t = useT();
  const byRank = (rank: number) => entries.find((e) => e.rank === rank);
  // Ten ranks, so the whole ceremony fits any presenter's screen at a glance
  // rather than needing to move to be read.
  const rest = entries.filter((e) => e.rank > 3 && e.rank <= PODIUM_SIZE);
  const others = Math.max(0, entries.length - Math.min(entries.length, PODIUM_SIZE));

  return (
    <div className="podium-ceremony">
      <h1 className="podium-title">{t("hostGame.finalResults")}</h1>
      <div className="podium-stage" aria-label={t("hostGame.topThree")}>
        <PodiumColumn entry={byRank(2)} place={2} t={t} />
        <PodiumColumn entry={byRank(1)} place={1} t={t} />
        <PodiumColumn entry={byRank(3)} place={3} t={t} />
      </div>
      {rest.length > 0 && (
        <ol className="podium-rest" aria-label={t("hostGame.remainingPlayers")}>
          {rest.map((entry) => (
            <li key={entry.playerId} className="podium-rest-row">
              <span className="podium-rest-rank">#{entry.rank}</span>
              <span className="podium-rest-name">{entry.nickname}</span>
              <span className="podium-rest-score">{entry.score.toLocaleString()} pts</span>
            </li>
          ))}
        </ol>
      )}
      {others > 0 && (
        <p className="podium-rest-footer">
          {others === 1
            ? t("hostGame.oneMorePlayerHidden")
            : t("hostGame.morePlayersHidden", { count: others })}
        </p>
      )}
    </div>
  );
}
